import { CircularProgress } from '@civilgrid/ui/components';
import { uniq, without } from 'lodash';
import { useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';
import { editRole } from '~/api/organizations';
import { Action } from '~/authorizations';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { OrganizationDetail } from '~/common/types';
import Checkbox from '~/components2/forms/Checkbox';
import InfoTooltip from '~/components2/InfoTooltip';
import { Notify } from '~/components2/Notify';
import { type ExportLabels, useExportLabels } from '~/hooks/useExportTerminologyEnabled';
import rootStore from '~/store';

interface Section {
  name: 'Organization' | 'Project' | 'Mobile';
  description: string;
  actions: ActionDescription[];
}

interface ActionDescription {
  id: Action;
  label: string;
  description?: string;
}

export const SECTIONS: { [key: string]: Section } = {
  Organization: {
    name: 'Organization',
    description: '',
    actions: [
      {
        id: Action.ViewOrgSettings,
        label: 'View organization settings',
      },
      {
        id: Action.ViewSubscriptionInfo,
        label: 'View subscription information',
      },
      {
        id: Action.InviteOrgMembers,
        label: 'Invite organization members',
        description: 'Can invite new members to same or lower role level',
      },
      {
        id: Action.UpdateOrgRoles,
        label: 'Update member roles',
        description: 'Can update to same or lower role level',
      },
      {
        id: Action.UpdateOrgPermissions,
        label: 'Update granular permissions of roles',
      },
      {
        id: Action.RemoveOrgMember,
        label: 'Remove member',
      },
      {
        id: Action.UpdateCompanyLogo,
        label: 'Update Logo',
      },
      {
        id: Action.ViewOrgTransactions,
        label: '($) View Transactions',
      },
      {
        id: Action.ManageOrgLayerConfig,
        label: 'Manage organization layer library',
      },
    ],
  },
  Project: {
    name: 'Project',
    description: '',
    actions: [
      {
        id: Action.CreateProject,
        label: '($) Create new projects',
        description: 'Create new project or upgrade project. Members without this permission can create drafts.',
      },
      {
        id: Action.ViewAllProjects,
        label: 'View all projects',
      },
      {
        id: Action.ViewProjectDetails,
        label: 'View project details',
      },
      {
        id: Action.EditProjectDetails,
        label: 'Edit project details',
      },
      {
        id: Action.EditProjectBoundary,
        label: '($) Edit project boundary',
      },
      {
        id: Action.ArchiveProject,
        label: 'Mark Complete or Archive Project',
      },
      {
        id: Action.UpdateProjectPM,
        label: 'Update project PM',
      },
      {
        id: Action.AddProjectMember,
        label: 'Add member to project',
      },
      {
        id: Action.RemoveProjectMember,
        label: 'Remove member from project',
      },
      {
        id: Action.ViewProjectTransactions,
        label: '($) View project Billing info',
      },
      {
        id: Action.AddProjectFile,
        label: 'Add files',
      },
      {
        id: Action.UpdateProjectFile,
        label: 'Update files',
        description: 'Update other users files in project',
      },
      {
        id: Action.DeleteProjectFile,
        label: 'Delete files',
      },
      {
        id: Action.AddFieldData,
        label: 'Add field data',
      },
      {
        id: Action.UpdateFieldData,
        label: 'Update field data',
        description: 'Update other users field data in project',
      },
      {
        id: Action.DeleteFieldData,
        label: 'Delete field data',
      },
      {
        id: Action.OrderFreeReports,
        label: 'Order free reports',
      },
      {
        id: Action.OrderPaidReports,
        label: '($) Order paid reports',
      },
      {
        id: Action.DownloadData,
        label: 'Download data',
      },
      {
        id: Action.RequestMaps,
        label: 'Request data (records research)',
      },
      {
        id: Action.ExportOutreachLog,
        label: 'Export outreach log',
      },
      {
        id: Action.ViewCivilDraw,
        label: 'View all CivilDraw drawings',
      },
      {
        id: Action.CreateCivilDraw,
        label: 'Create CivilDraw drawing',
      },
      {
        id: Action.EditCivilDraw,
        label: 'Edit CivilDraw drawings',
      },
      {
        id: Action.DeleteCivilDraw,
        label: 'Delete CivilDraw drawings',
      },
      {
        id: Action.CommentCivilDraw,
        label: 'Comment on CivilDraw drawings',
      },
    ],
  },
  Mobile: {
    name: 'Mobile',
    description: '',
    actions: [
      {
        id: Action.MobileViewAssets,
        label: 'View mobile assets',
      },
      {
        id: Action.MobileAddData,
        label: 'Add field data',
      },
      {
        id: Action.MobileEditData,
        label: 'Edit field data',
        description: 'Update other users field data',
      },
      {
        id: Action.MobileDeleteData,
        label: 'Delete field data',
        description: 'Update other users field data',
      },
    ],
  },
};

/**
 * SECTIONS is module-level, so the one flag-renamed label is resolved per render. Every surface
 * that renders these actions must go through here, or two screens name the same permission
 * differently once the flag is on.
 */
export function resolveActionLabel(action: { id: Action; label: string }, labels: ExportLabels): string {
  return action.id === Action.DownloadData ? labels.rolePermissionLabel : action.label;
}

export default function Roles() {
  const { currentOrganizationDetail } = useSnapshot(rootStore) as typeof rootStore;
  const [roles, setRoles] = useState<OrganizationDetail['roles']>([]);
  const labels = useExportLabels();

  useEffect(() => {
    if (!currentOrganizationDetail.isLoading) {
      setRoles(currentOrganizationDetail.roles);
    }
  }, [currentOrganizationDetail]);

  if (roles.length === 0) {
    return <CircularProgress indeterminate />;
  }

  function getSection(sectionId: Section['name']) {
    const section = SECTIONS[sectionId];
    if (!section) {
      return null;
    }

    return (
      <div>
        <div className="py-2 pl-1 text-base font-semibold">{section.name}</div>
        <table className="relative table-auto">
          <thead>
            {/* -top-4 offsets the scroll container's py-4 so the header sticks flush; pt-4 on th keeps content aligned */}
            <tr className="sticky -top-4 z-10 bg-white text-left text-sm font-bold h-8">
              <th className="pt-4"></th>
              {roles.map((role) => (
                <th className="py-4 pr-6 text-sm text-grey-90 font-bold" key={role.id}>
                  {role.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {section.actions.map((action) => (
              <tr key={action.id} className="hover:bg-secondary/5 h-8">
                <td className="w-96 text-sm">
                  <div className="flex items-center space-x-1 pl-1">
                    <div>{resolveActionLabel(action, labels)}</div>
                    {action.description && (
                      <InfoTooltip text={action.description} label={`About ${resolveActionLabel(action, labels)}`} />
                    )}
                  </div>
                </td>

                {roles.map((role) => (
                  <td key={role.id} className="text-center">
                    <Checkbox
                      label={''}
                      data-testid={`org-role-permission-${role.roleId}-${action.id}`}
                      value={role.actions.includes(action.id)}
                      checked={role.actions.includes(action.id)}
                      onChange={async (isChecked) => {
                        capturePosthogEvent(POSTHOG_EVENTS.organizationPermissionUpdated, {
                          organization_id: (currentOrganizationDetail as OrganizationDetail).id,
                          added: isChecked,
                          role_id: role.roleId,
                          role_name: role.name,
                          action: action.id,
                        });

                        const updatedRoles: OrganizationDetail['roles'] = JSON.parse(JSON.stringify(roles));
                        const r = updatedRoles.find((r) => r.roleId === role.roleId);

                        if (r) {
                          if (isChecked) {
                            r.actions = uniq([...r.actions, action.id]);
                          } else {
                            r.actions = without(r.actions, action.id);
                          }
                        }

                        setRoles(updatedRoles);

                        const orgId = (currentOrganizationDetail as OrganizationDetail).id;

                        await editRole(orgId, role.roleId, {
                          actions: r!.actions,
                        });

                        rootStore.setCurrentOrganization(orgId);

                        Notify.info({
                          title: 'Role Updated',
                          message: `<div>Role <span className="font-semibold">${role.name}</span> updated</div>`,
                        });
                      }}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="space-y-8 p-2 pb-48">
      {getSection('Organization')}
      {getSection('Project')}
      {getSection('Mobile')}
    </div>
  );
}
